import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Loader2, Inbox, Star } from "@/components/icons";

export function Logo({ size = 30, className }) {
  return (
    <img
      src="/sofit-wordmark.png"
      alt="Sofit"
      data-testid="sofit-logo"
      draggable={false}
      className={cn("select-none w-auto object-contain", className)}
      style={{ height: Math.round(size * 0.86) }}
    />
  );
}

const VARIANTS = {
  primary: "bg-brand text-white hover:bg-brand-deep",
  outline: "bg-white text-ink border-2 border-ink hover:bg-ink hover:text-white",
  dark: "bg-ink text-white hover:bg-black",
  ghost: "bg-brand-soft text-brand hover:bg-brand-soft2",
  danger: "bg-destructive text-white hover:opacity-90",
};

export function Button({ title, children, onClick, variant = "primary", icon: Icon, loading, disabled, testID, small, className, type = "button", full }) {
  return (
    <button
      type={type}
      data-testid={testID}
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-pill font-display font-semibold transition-all active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none",
        small ? "min-h-[40px] px-4 text-sm" : "min-h-[52px] px-6 text-base",
        full && "w-full",
        VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Loader2 className="animate-spin" size={18} /> : (<>{title || children}{Icon ? <Icon size={18} /> : null}</>)}
    </button>
  );
}

export function Chip({ label, selected, onClick, testID, icon: Icon }) {
  return (
    <button data-testid={testID} onClick={onClick} className={cn(
      "inline-flex shrink-0 items-center gap-1.5 h-9 px-4 rounded-pill font-body font-semibold text-[13px] transition-colors",
      selected ? "bg-brand text-white" : "bg-brand-soft text-brand hover:bg-brand-soft2",
    )}>
      {Icon ? <Icon size={14} /> : null}{label}
    </button>
  );
}

export function Tag({ label, tone = "pink", className }) {
  const tones = {
    pink: "bg-brand-soft text-brand", dark: "bg-ink text-white", green: "bg-success text-white", grey: "bg-surface2 text-mutedgrey",
  };
  return <span className={cn("inline-flex items-center h-6 px-2.5 rounded-pill text-[11px] font-body font-semibold uppercase tracking-wide", tones[tone], className)}>{label}</span>;
}

export function Stars({ rating, size = 14, count }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(rating) ? "fill-brand text-brand" : "text-line"} />
      ))}
      <span className="ml-1 font-body font-semibold text-ink" style={{ fontSize: size - 1 }}>
        {Number(rating || 0).toFixed(1)}{count !== undefined ? ` (${count})` : ""}
      </span>
    </span>
  );
}

export function StarPicker({ value, onChange, testID, size = 34 }) {
  return (
    <div className="flex gap-2" data-testid={testID}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} data-testid={`${testID}-${i}`} onClick={() => onChange(i)}>
          <Star size={size} className={i <= value ? "fill-brand text-brand" : "text-line"} />
        </button>
      ))}
    </div>
  );
}

export function Avatar({ uri, size = 40, name, className }) {
  if (!uri) {
    return (
      <div className={cn("flex items-center justify-center rounded-full bg-brand-soft text-brand font-display font-bold shrink-0", className)}
        style={{ width: size, height: size, fontSize: size * 0.4 }}>
        {(name ?? "?").slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return <img src={uri} alt={name || "avatar"} className={cn("rounded-full object-cover shrink-0", className)} style={{ width: size, height: size }} />;
}

export function SectionHeader({ title, subtitle, action, to, onAction, testID }) {
  return (
    <div className="flex items-end justify-between mb-4 gap-4">
      <div>
        <h2 className="font-display text-2xl text-ink tracking-tight">{title}</h2>
        {subtitle ? <p className="font-body text-sm text-mutedgrey mt-0.5">{subtitle}</p> : null}
      </div>
      {action ? (
        to ? (
          <Link data-testid={testID} to={to} className="flex items-center gap-1 font-body font-semibold text-sm text-brand shrink-0">{action}<span aria-hidden>{"→"}</span></Link>
        ) : (
          <button data-testid={testID} onClick={onAction} className="flex items-center gap-1 font-body font-semibold text-sm text-brand shrink-0">{action}<span aria-hidden>{"→"}</span></button>
        )
      ) : null}
    </div>
  );
}

export function Field({ label, testID, error, className, as = "input", ...props }) {
  const Cmp = as;
  return (
    <label className="flex flex-col gap-1.5">
      {label ? <span className="font-body font-semibold text-[13px] text-ink">{label}</span> : null}
      <Cmp data-testid={testID} {...props}
        className={cn("w-full rounded-md border border-line bg-surface2 px-4 font-body text-[15px] text-ink outline-none focus:border-brand transition-colors", as === "textarea" ? "py-3 min-h-[100px]" : "h-[52px]", className)} />
      {error ? <span className="font-body text-xs text-destructive">{error}</span> : null}
    </label>
  );
}

export function StatTile({ label, value, testID }) {
  return (
    <div data-testid={testID} className="flex-1 min-w-[140px] rounded-xl2 bg-brand-soft p-4">
      <div className="font-display text-[26px] text-brand tracking-tight">{value}</div>
      <div className="font-body text-[13px] text-ink mt-0.5">{label}</div>
    </div>
  );
}

export function Loading({ className }) {
  return (
    <div className={cn("flex items-center justify-center p-10", className)} data-testid="loading-indicator">
      <Loader2 className="animate-spin text-brand" size={36} />
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, subtitle }) {
  return (
    <div className="flex flex-col items-center gap-2 p-10 text-center" data-testid="empty-state">
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-brand-soft mb-2"><Icon className="text-brand" size={26} /></div>
      <h3 className="font-display text-lg text-ink">{title}</h3>
      {subtitle ? <p className="font-body text-sm text-mutedgrey max-w-xs">{subtitle}</p> : null}
    </div>
  );
}

export function Card({ children, className, ...rest }) {
  return <div className={cn("rounded-xl2 bg-white border border-divider", className)} {...rest}>{children}</div>;
}
